import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from 'antd'
import { useSelector } from 'react-redux'

import getTerminology from '@/utils/getTerminology'
import { preloadWafScript } from '@/utils/wafChallenge'
import {
	registerWafCaptchaPresenter,
	WAF_FALLBACK_ERROR_MESSAGE,
} from '@/utils/wafCaptchaPresenter'

const WafCaptchaModal = () => {
	const containerRef = useRef(null)
	const pendingRef = useRef(null)
	const errorMessageRef = useRef(WAF_FALLBACK_ERROR_MESSAGE)
	const [request, setRequest] = useState(null)
	const [captchaContainer, setCaptchaContainer] = useState(null)
	const wafConfig = useSelector((state) => state.settings?.waf_challenge)
	const saveContainer = useCallback((element) => {
		containerRef.current = element
		setCaptchaContainer(element)
	}, [])

	const finish = useCallback((error) => {
		const pending = pendingRef.current
		pendingRef.current = null
		setRequest(null)
		containerRef.current?.replaceChildren()

		if (!pending) return
		if (error) pending.reject(error)
		else pending.resolve()
	}, [])

	useEffect(() => {
		return registerWafCaptchaPresenter((options) => {
			return new Promise((resolve, reject) => {
				if (pendingRef.current) {
					reject(new Error(options?.errorMessage || WAF_FALLBACK_ERROR_MESSAGE))
					return
				}

				pendingRef.current = { resolve, reject }
				errorMessageRef.current = options?.errorMessage || WAF_FALLBACK_ERROR_MESSAGE
				setRequest(options)
			})
		})
	}, [])

	useEffect(() => {
		if (!request || !captchaContainer) return

		const failCaptcha = (cause) => {
			const error = new Error(request.errorMessage)
			if (cause) error.cause = cause
			finish(error)
		}

		const renderCaptcha = window.AwsWafCaptcha?.renderCaptcha
		if (typeof renderCaptcha !== 'function') {
			failCaptcha(new Error('AWS WAF CAPTCHA API is missing.'))
			return
		}

		try {
			renderCaptcha(captchaContainer, {
				apiKey: request.apiKey,
				dynamicWidth: true,
				skipTitle: true,
				onSuccess: () => finish(),
				onError: failCaptcha,
			})
		} catch (error) {
			failCaptcha(error)
		}
	}, [captchaContainer, finish, request])

	useEffect(() => {
		if (wafConfig?.enabled !== true || !wafConfig?.jsapi_url) return

		// Start the silent check early so submit does not wait for the script.
		preloadWafScript().catch((error) => console.warn(error.message))
	}, [wafConfig?.enabled, wafConfig?.jsapi_url])

	useEffect(() => {
		return () => {
			const pending = pendingRef.current
			pendingRef.current = null
			pending?.reject(new Error(errorMessageRef.current))
		}
	}, [])

	const title = getTerminology('waf_verification_title', null, false, true) || 'Security check'
	const help =
		getTerminology('waf_verification_help', null, false, true) ||
		'Please complete the check to continue.'

	return (
		<Modal
			centered
			destroyOnHidden
			open={!!request}
			title={title}
			footer={null}
			maskClosable={false}
			onCancel={() => finish(new Error(request?.errorMessage || WAF_FALLBACK_ERROR_MESSAGE))}
			width={420}
			styles={{
				mask: { backdropFilter: 'blur(6px)' },
				body: { textAlign: 'center' },
			}}
		>
			<p>{help}</p>
			<div ref={saveContainer} className='flex min-h-48 w-full justify-center' />
		</Modal>
	)
}

export default WafCaptchaModal
